/**
 * Room Transform Hook
 * 
 * Handles room drag and resize operations with validation
 * Date: 09-10-2025 12:40:09
 * 
 * @module floor-plan-editor/useRoomTransform
 */

import { useCallback } from 'react';
import type { KonvaEventObject } from 'konva/lib/Node';
import type { RoomTransformData } from '../../../types/room-layout';
import {
  snapToGrid,
  constrainToBounds,
  detectOverlaps,
  calculateCanvasBounds,
  validateRoomDimensions
} from './layoutValidation';

/**
 * Room position interface
 */
interface RoomPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  roomId?: string;
}

/**
 * Hook configuration
 */
interface UseRoomTransformConfig {
  rooms: RoomPosition[];
  stageWidth: number;
  stageHeight: number;
  gridSize?: number;
  enableSnap?: boolean;
  onTransform: (data: RoomTransformData) => void;
  onOverlapDetected?: (overlappingRoomIds: string[]) => void;
}

/**
 * Hook for managing room transform operations
 * 
 * @param config - Hook configuration
 * @returns Transform handlers and utilities
 */
export function useRoomTransform(config: UseRoomTransformConfig) {
  const {
    rooms,
    stageWidth,
    stageHeight,
    gridSize = 20,
    enableSnap = true,
    onTransform,
    onOverlapDetected
  } = config;

  const canvasBounds = calculateCanvasBounds(stageWidth, stageHeight, 20);

  /**
   * Handle room drag end event
   * 
   * @param roomId - Room ID being dragged
   * @param e - Konva event object
   */
  const handleDragEnd = useCallback(
    (roomId: string, e: KonvaEventObject<DragEvent>) => {
      const node = e.target;
      let x = node.x();
      let y = node.y();

      // Get room dimensions from node
      const width = node.width() * node.scaleX();
      const height = node.height() * node.scaleY();

      // Snap to grid if enabled
      if (enableSnap) {
        x = snapToGrid(x, gridSize);
        y = snapToGrid(y, gridSize);
      }

      // Constrain to canvas bounds
      const constrained = constrainToBounds(
        { x, y, width, height, roomId },
        canvasBounds
      );

      // Update node position (visual feedback)
      node.x(constrained.x);
      node.y(constrained.y);

      // Check for overlaps
      const overlapResult = detectOverlaps(
        { x: constrained.x, y: constrained.y, width, height, roomId },
        rooms,
        roomId
      );

      if (overlapResult.hasOverlap && onOverlapDetected) {
        onOverlapDetected(overlapResult.overlappingRoomIds);
      }

      // Trigger transform callback
      onTransform({
        roomId,
        x: constrained.x,
        y: constrained.y
      });
    },
    [rooms, canvasBounds, gridSize, enableSnap, onTransform, onOverlapDetected]
  );

  /**
   * Handle room transform end event (resize/rotate)
   * 
   * @param roomId - Room ID being transformed
   * @param e - Konva event object
   */
  const handleTransformEnd = useCallback(
    (roomId: string, e: KonvaEventObject<Event>) => {
      const group = e.target as any;  // Cast to access Group methods

      // Find the Rect child inside the Group
      const rectNode = group.findOne('Rect');
      if (!rectNode) {
        console.warn('Rect node not found in Group');
        return;
      }

      // Get scale from Group
      const scaleX = group.scaleX();
      const scaleY = group.scaleY();
      
      // Calculate new dimensions from Rect's current width/height * scale
      const newWidth = Math.max(50, rectNode.width() * scaleX);
      const newHeight = Math.max(50, rectNode.height() * scaleY);

      // Reset Group scale to 1 (so scale doesn't compound)
      group.scaleX(1);
      group.scaleY(1);

      // Snap dimensions to grid if enabled
      let finalWidth = newWidth;
      let finalHeight = newHeight;
      
      if (enableSnap) {
        finalWidth = snapToGrid(newWidth, gridSize);
        finalHeight = snapToGrid(newHeight, gridSize);
      }

      // Validate dimensions
      const validation = validateRoomDimensions(finalWidth, finalHeight);
      if (!validation.isValid) {
        console.warn('Invalid dimensions:', validation.error);
        // Revert to previous size
        return;
      }

      // Update Rect dimensions directly (not Group!)
      rectNode.width(finalWidth);
      rectNode.height(finalHeight);
      
      // Update text elements to match new size
      const texts = group.find('Text');
      texts.forEach((text: any, index: number) => {
        text.width(finalWidth);
        if (index === 0) {
          text.y(finalHeight / 2 - 12);
        } else {
          text.y(finalHeight / 2 + 4);
        }
      });

      // Get position
      const x = group.x();
      const y = group.y();

      // Force redraw
      group.getLayer()?.batchDraw();

      // Check for overlaps
      const overlapResult = detectOverlaps(
        { x, y, width: finalWidth, height: finalHeight, roomId },
        rooms,
        roomId
      );

      if (overlapResult.hasOverlap && onOverlapDetected) {
        onOverlapDetected(overlapResult.overlappingRoomIds);
      }

      // Trigger transform callback with final dimensions
      onTransform({
        roomId,
        width: finalWidth,
        height: finalHeight,
        x,
        y
      });
    },
    [rooms, gridSize, enableSnap, onTransform, onOverlapDetected]
  );

  /**
   * Create drag bound function for Konva node
   * Simplified version to fix dragging regression
   * @created 17-10-2025 07:30:00
   * 
   * @param roomWidth - Room width
   * @param roomHeight - Room height
   * @returns Drag bound function
   */
  const createDragBoundFunc = useCallback(
    (roomWidth: number, roomHeight: number) => {
      return (pos: { x: number; y: number }) => {
        // Simple bounds calculation without pre-calculation
        const minX = canvasBounds.minX;
        const minY = canvasBounds.minY;
        const maxX = canvasBounds.maxX - roomWidth;
        const maxY = canvasBounds.maxY - roomHeight;
        
        return {
          x: Math.max(minX, Math.min(pos.x, maxX)),
          y: Math.max(minY, Math.min(pos.y, maxY))
        };
      };
    },
    [canvasBounds]
  );

  /**
   * Check if specific room has overlaps
   * 
   * @param roomId - Room ID to check
   * @param position - Room position to check
   * @returns true if room overlaps with others
   */
  const hasOverlaps = useCallback(
    (roomId: string, position: RoomPosition): boolean => {
      const result = detectOverlaps(position, rooms, roomId);
      return result.hasOverlap;
    },
    [rooms]
  );

  return {
    handleDragEnd,
    handleTransformEnd,
    createDragBoundFunc,
    hasOverlaps
  };
}


